import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useMerch } from '../../contexts/MerchContext';
import { CheckCircle, ArrowRight, Home, ShoppingBag, Loader } from 'lucide-react';
import Header from '../../components/ui/Header';

const MerchSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { clearCart } = useMerch();
    const [status, setStatus] = useState('loading'); // loading, success, error
    const [message, setMessage] = useState('Verifying payment...');

    useEffect(() => {
        const paymentIntent = searchParams.get('payment_intent');
        const clientSecret = searchParams.get('payment_intent_client_secret');
        const redirectStatus = searchParams.get('redirect_status');

        if (!paymentIntent) {
            // No payment intent in URL, maybe direct access?
            setStatus('error');
            setMessage('No order information found.');
            return;
        }

        if (redirectStatus === 'succeeded') {
            setStatus('success');
            setMessage('Payment confirmed! Your order has been placed.');
            clearCart();
        } else {
            setStatus('error');
            setMessage('Payment processing failed or was cancelled.');
        }
    }, [searchParams, clearCart]);

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <div className="pt-32 pb-12 flex items-center justify-center p-4">
                <div className="bg-card border border-border rounded-xl p-8 max-w-md w-full text-center shadow-gaming-lg">

                    {status === 'loading' && (
                        <div className="flex flex-col items-center">
                            <Loader className="w-16 h-16 text-primary animate-spin mb-6" />
                            <h1 className="text-2xl font-bold text-foreground mb-2">Processing...</h1>
                            <p className="text-muted-foreground">{message}</p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="flex flex-col items-center">
                            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-6">
                                <CheckCircle className="w-10 h-10 text-green-500" />
                            </div>
                            <h1 className="text-3xl font-bold text-foreground mb-4">Order Confirmed!</h1>
                            <p className="text-muted-foreground mb-8">
                                Thank you for your purchase. You will receive an email confirmation shortly.
                            </p>

                            <div className="space-y-3 w-full">
                                <Link
                                    to="/user-profile-settings?tab=orders"
                                    className="flex items-center justify-center w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
                                >
                                    <ShoppingBag className="w-4 h-4 mr-2" />
                                    View My Orders
                                </Link>
                                <Link
                                    to="/browse"
                                    className="flex items-center justify-center w-full px-6 py-3 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors font-medium"
                                >
                                    <Home className="w-4 h-4 mr-2" />
                                    Return to Home
                                </Link>
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="flex flex-col items-center">
                            <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mb-6">
                                <div className="text-3xl">⚠️</div>
                            </div>
                            <h1 className="text-2xl font-bold text-foreground mb-4">Something went wrong</h1>
                            <p className="text-muted-foreground mb-8">{message}</p>

                            <button
                                onClick={() => navigate('/merch/checkout')}
                                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
                            >
                                Return to Checkout
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MerchSuccess;
